import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Contact } from "@interfaces/dws/contact";

const TAGS_MAX_VISIBLE = 8;

@Component({
  selector: "app-client-contact-autocomplete-option",
  templateUrl: "./client-contact-autocomplete-option.component.html",
  styleUrls: ["./client-contact-autocomplete-option.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientContactAutocompleteOptionComponent {
  @Input() contact!: Contact;
  /** BCP 47 base language for number/region formatting (e.g. from host `lang`). */
  @Input() displayLocale = "en";

  constructor(private translate: TranslateService) {}

  get primaryName(): string {
    const c = this.contact;
    const company = c.companyName?.trim();
    if (company) {
      return company;
    }
    return (c.name || "").trim() || "—";
  }

  get subtitle(): string | null {
    const c = this.contact;
    const company = c.companyName?.trim();
    const n = (c.name || "").trim();
    if (company && n && n.toLowerCase() !== company.toLowerCase()) {
      return n;
    }
    return null;
  }

  get phoneDisplay(): string | undefined {
    const p = this.contact.phone?.trim();
    return p || undefined;
  }

  get evaluation(): number | undefined {
    const e = this.contact.evaluation;
    return e !== undefined && e !== null ? e : undefined;
  }

  get customerValueDisplay(): string | null {
    if (this.evaluation !== undefined) {
      return null;
    }
    const raw = (this.contact as any).customerValue as unknown;
    if (typeof raw !== "number" || Number.isNaN(raw) || raw === 0) {
      return null;
    }
    try {
      return new Intl.NumberFormat(this.localeForFormat(), { maximumFractionDigits: 0 }).format(raw);
    } catch {
      return String(raw);
    }
  }

  get showValueChips(): boolean {
    return this.evaluation !== undefined || this.customerValueDisplay != null;
  }

  get country(): { iso: string; label: string } | null {
    const iso = this.resolveCountryIso();
    if (!iso) {
      return null;
    }
    return { iso, label: this.regionLabel(iso) };
  }

  get tagsPreview(): { tags: string[]; more: number } | null {
    const all = this.tagsRawList();
    if (all.length === 0) {
      return null;
    }
    if (all.length <= TAGS_MAX_VISIBLE) {
      return { tags: all, more: 0 };
    }
    return { tags: all.slice(0, TAGS_MAX_VISIBLE), more: all.length - TAGS_MAX_VISIBLE };
  }

  private localeForFormat(): string {
    return (this.translate.currentLang || this.displayLocale || "en").split("-")[0];
  }

  private resolveCountryIso(): string | undefined {
    const c = this.contact;
    const iso = (c.countryOfResidenceIso || "").trim();
    if (iso.length === 2 && /^[a-zA-Z]{2}$/.test(iso)) {
      return iso.toUpperCase();
    }
    const cc = (c.countryCode || "").trim();
    if (cc.length === 2 && /^[a-zA-Z]{2}$/.test(cc)) {
      return cc.toUpperCase();
    }
    return undefined;
  }

  private regionLabel(iso: string): string {
    try {
      const IDisplayNames = (Intl as any).DisplayNames;
      if (typeof IDisplayNames !== "function") {
        return iso;
      }
      const lang = this.localeForFormat();
      return new IDisplayNames([lang], { type: "region" }).of(iso) || iso;
    } catch {
      return iso;
    }
  }

  private tagsRawList(): string[] {
    const c = this.contact;
    const anyTags = (c as any).tags;
    if (Array.isArray(anyTags)) {
      return anyTags
        .map((x: any) => (typeof x === "string" ? x : x?.name || x?.tag?.name || ""))
        .map((s: string) => String(s).trim())
        .filter((s: string) => s.length > 0);
    }
    const s = c.tags;
    if (!s || typeof s !== "string") {
      return [];
    }
    return s
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }
}
